import React, { useEffect, useState } from "react";
import { BrowserRouter, Switch, Redirect, Route, useLocation } from "react-router-dom";
import { isSignedIn, isSimulating } from "@culturehq/client";
import { classnames } from "@culturehq/components";

import "app/client";
import { useProfile } from "app/context";
import { useEventStartingAlert, useFavicon } from "app/hooks";

import ErrorBoundary from "components/common/ErrorBoundary";
import Navigation from "components/Navigation";
import SimulationBanner from "components/internal/SimulationBanner";
import PrivateRoute from "components/common/PrivateRoute";
import LazyTargeting from "components/common/LazyTargeting";
import EventSurveyPrompt from "components/EventSurveyPrompt";

import AnnouncementCallout from "components/callouts/AnnouncementCallout";
import AnnouncementUserActivityCallout from "components/callouts/AnnouncementUserActivityCallout";
import EndorsementCallout from "components/callouts/EndorsementCallout";
import LazyCommentCallout from "components/callouts/LazyCommentCallout";
import PostCallout from "components/callouts/PostCallout";
import StoryTrendCallout from "components/callouts/StoryTrendCallout";
import SurveyCallout from "components/callouts/SurveyCallout";
import TakeSurveyCallout from "components/callouts/TakeSurveyCallout";
import UserClaimedCallout from "components/callouts/UserClaimedCallout";

import EventUpdate from "components/events/EventUpdate";
import Gallery from "components/common/Gallery";
import Community from "components/Community";
import Person from "components/Person";
import RecognitionRoutes from "components/recognitions/RecognitionRoutes";
import Points from "components/Points";
import Profile from "components/Profile";
import ProfileOrganizations from "components/ProfileOrganizations";
import Surveys from "components/survey/Surveys";
import SurveyEdit from "components/survey/SurveyEdit";
import Welcome from "components/Welcome";

import ResetPassword from "components/ResetPassword";
import ResetPasswordRequest from "components/ResetPasswordRequest";

import Login from "components/Login";
import Logout from "components/Logout";
import SSO from "components/login/SSO";
import SessionTimeout from "components/SessionTimeout";
import Security from "components/Security";
import UserNetwork from "components/UserNetwork";

import { ClickTransportProvider } from "app/context/ClickTransportContext";
import { CompanyProvider } from "app/context/CompanyContext";
import { DepartmentsProvider } from "app/context/DepartmentsContext";
import { InterestsProvider } from "app/context/InterestsContext";
import { LocationsProvider } from "app/context/LocationsContext";
import { SkillsProvider } from "app/context/SkillsContext";
import { ClassYearsProvider } from "app/context/ClassYearsContext";
import { ClubsProvider } from "app/context/ClubsContext";
import { ProgramsProvider } from "app/context/ProgramsContext";
import { DegreesProvider } from "app/context/ContextDegrees";
import { ProfileProvider } from "app/context/ProfileContext";
import { RecognitionTypesProvider } from "app/context/RecognitionTypesContext";
import { ValuesProvider } from "app/context/ValuesContext";
import { ModalProvider } from "app/context/ModalContext";
import { PublicProvider } from "app/context/PublicContext";
import { UserActivitiesProvider } from "app/context/UserActivitiesContext";

import { isPlatformSubdomain } from "app/utils";

import InboundLink from "components/InboundLink";
import Forbidden from "components/Forbidden";
import NotFound from "components/NotFound";

import Feed from "./Feed";
import Home from "./Home";
import Events from "./Events";
import Featured from "./Featured";
import Event from "./Event";
import SlackLink from "./SlackLink";
import MSALLogin from "./MSALLogin";
import MSALLoginSignup from "./MSALLoginSignup";
import Trends from "./Trends";
import AdminSettings from "./admin/AdminSettings";
import EmbedStoryCallout from "./callouts/EmbedStoryCallout";
import LandingPageCallout from "./callouts/LandingPageCallout";
import LandingPageUserCallout from "./callouts/LandingPageUserCallout";
import LandingPagePreviewCallout from "./callouts/LandingPagePreviewCallout";
import LandingPageUserPreviewCallout from "./callouts/LandingPageUserPreviewCallout";
import LazyGoogleAnalytics from "./common/LazyGoogleAnalytics";
import LandingPageStoriesCallout from "./callouts/LandingPageStoriesCallout";
import LandingPageTrendCallout from "./callouts/LandingPageTrendCallout";
import LazyUserActivities from "./callouts/LazyUserActivities";
import NotMobileAppInstalled from "./NotMobileAppInstalled";

const PeopleRedirect = ({ match, location }) => (
  <Redirect to={{ pathname: `/community/${match.params.user}`, hash: location.hash }} />
);

const AppWithContext = React.memo(() => {
  const { profile, refreshProfile } = useProfile();
  const location = useLocation();
  const [path, setPath] = useState();
  const previewPage = "/admin/landing-page/preview";
  useEventStartingAlert();

  useEffect(() => {
    if (!isSignedIn() || profile.id) return;
    refreshProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id]);

  useEffect(
    () => {
      setPath(location.pathname.split("/")[1]);
    }, [location.pathname]
  );

  /*
  * Receive push notifications when the page is focused, but we do have sockets for that
  */
  /*
  onMessageListener()
    .then(payload => {
      const { title, body } = payload.data;
    })
    .catch(err => {;});
  */
  const allowedPathsWithNav = [
    "", "stories", "new", "comments", "spotlights", "events", "featured", "feed", "trends", "logout", "community",
    "people", "photos", "points", "posts", "surveys", "profile", "recognitions", "score", "security",
    "slack", "users_claimed", "admin", "activity"
  ];

  useFavicon();
  const subdomain = window.location.host.split(".")[0];
  if (((!isPlatformSubdomain(subdomain) && process.env.NODE_ENV === "production")
    || (subdomain !== "localhost:4000" && process.env.NODE_ENV !== "production")
    || window.location.pathname === "/profile/organizations")
    && process.env.NODE_ENV !== "test") {
    return (
      <>
        {process.env.NODE_ENV === "production" && !(subdomain === "intellia-therapeutics" && window.self !== window.top) && <LazyGoogleAnalytics />}
        <div className="content">
          <ErrorBoundary>
            <Switch>
              <Route exact path="/" component={Home} />
              <Route exact path="/users/:userId" render={() => <LandingPageUserCallout orgName={subdomain} />} />
              <Route exact path="/users/:userId/story/:storyId" render={() => <LandingPageUserCallout orgName={subdomain} />} />
              <Route exact path="/trends/:trendId" render={() => <LandingPageTrendCallout orgName={subdomain} />} />
              <Route exact path="/story/:storyId" render={() => <LandingPageCallout orgName={subdomain} />} />
              <Route exact path="/stories" render={() => <LandingPageStoriesCallout orgName={subdomain} />} />
              <PrivateRoute exact path="/profile/organizations" component={ProfileOrganizations} />

              <Route component={NotFound} />
            </Switch>
          </ErrorBoundary>
        </div>
      </>
    );
  }

  return (
    <>
      {process.env.NODE_ENV === "production" && subdomain !== "localhost:4000" && !isSimulating() && <LazyTargeting />}
      {isSignedIn() && profile.claimed && !location.pathname.startsWith(previewPage)
        && allowedPathsWithNav.includes(path) && (
        <>
          <SessionTimeout />
          <SimulationBanner />
          <Navigation />
          {!isSimulating() && <EventSurveyPrompt />}
        </>
      )}
      <div className={classnames("content", { [`${profile?.organization?.name?.replace(/\s+/g, "-")?.toLowerCase()}-content`]: profile.organization.name })}>
        <ErrorBoundary>
          <Switch>
            <Route exact path="/" component={Home} />
            <PrivateRoute exact path="/stories/:storyId" component={Home} />
            <PrivateRoute exact path="/stories/:storyId/share" component={Home} />
            <PrivateRoute exact path="/stories/:storyId/engage" component={Home} />
            <PrivateRoute exact path="/new/stories/:questionStoryId" component={Home} />
            <PrivateRoute exact path="/comments/:commentId" component={LazyCommentCallout} />
            <PrivateRoute exact path="/spotlights/:endorsementId" component={EndorsementCallout} />
            <PrivateRoute exact requires="events" path="/events" component={Events} />
            <PrivateRoute exact requires="events" path="/events/new/:eventForm" component={EventUpdate} />
            <PrivateRoute requires="events" path="/events/:eventId" component={Event} />
            <PrivateRoute exact path="/featured" component={Featured} />
            <PrivateRoute exact path="/featured/:announcementId" component={AnnouncementCallout} />
            <PrivateRoute exact path="/featured/:announcementId/show" component={AnnouncementUserActivityCallout} />
            <PrivateRoute exact path="/activity/:userActivityId" component={LazyUserActivities} />
            <PrivateRoute exact path="/feed" component={Feed} />
            <PrivateRoute exact path="/trends" component={Trends} />
            <PrivateRoute exact path="/logout" component={Logout} />

            <PrivateRoute exact path="/community/:userId/network" component={UserNetwork} />
            <PrivateRoute path="/community/:user" component={Person} />
            <PrivateRoute exact path="/community" component={Community} />
            <Route path="/people/:user" component={PeopleRedirect} />
            <Redirect from="/people" to="/community" />

            <PrivateRoute exact path="/photos" component={Gallery} />
            <PrivateRoute exact path="/photos/:photoId" component={Home} />
            <PrivateRoute exact path="/points" component={Points} />
            <PrivateRoute requires="posts" exact path="/posts/:postId" component={PostCallout} />
            <PrivateRoute exact path="/surveys/:surveyId/show" component={SurveyCallout} />
            <Route exact path="/stories/trend/:storyQuestionId" component={StoryTrendCallout} />
            <Route exact path="/stories/trend/:storyQuestionId/prompt/:promptId" component={StoryTrendCallout} />
            <PrivateRoute exact path="/stories/trend/:storyQuestionId/story/:storyId" component={StoryTrendCallout} />
            <PrivateRoute exact path="/stories/trend/:storyQuestionId/story/:storyId/share" component={StoryTrendCallout} />
            <PrivateRoute exact path="/stories/trend/:storyQuestionId/story/:storyId/engage" component={StoryTrendCallout} />
            <PrivateRoute exact path="/profile/network" component={UserNetwork} />
            <PrivateRoute path="/profile" component={Profile} />
            <PrivateRoute requires="recognitions" path="/recognitions" component={RecognitionRoutes} />
            <PrivateRoute exact path="/score" component={Points} />
            <PrivateRoute exact path="/security" component={Security} />
            <PrivateRoute exact path="/slack/link" component={SlackLink} />
            <PrivateRoute exact path="/surveys/:surveyId/edit" component={SurveyEdit} />
            <PrivateRoute exact path="/surveys/:surveyId/take" component={TakeSurveyCallout} />
            <PrivateRoute exact path="/users_claimed/:userClaimedId" component={UserClaimedCallout} />
            <PrivateRoute requires="surveys" path="/surveys" component={Surveys} />

            <PrivateRoute requires="superadmin" path="/admin/landing-page/preview/users/:userId" component={LandingPageUserPreviewCallout} />
            <PrivateRoute requires="superadmin" path="/admin/landing-page/preview" component={LandingPagePreviewCallout} />
            <PrivateRoute requires="admin" path="/admin" component={AdminSettings} />

            <Route exact path="/story/:storyId" component={EmbedStoryCallout} />
            <Route exact path="/accept" component={Welcome} />
            <Route exact path="/accept/sso" component={Welcome} />
            <Route exact path="/accept/sso/:token" component={Welcome} />
            <Route exact path="/accept/sso/:token/*" component={Welcome} />
            <Route exact path="/accept/:token" component={Welcome} />
            <Route exact path="/accept/:token/*" component={Welcome} />
            <Route exact path="/reset_password/:token" component={ResetPassword} />
            <Route exact path="/reset_password/email/:email" component={ResetPasswordRequest} />
            <Route exact path="/saml" component={SSO} />
            <Route exact path="/sso" component={SSO} />
            <Route exact path="/sso/:token" component={SSO} />
            <Route exact path="/sso/:token/*" component={SSO} />
            <Route exact path="/social/:id/:social_media_type" component={NotMobileAppInstalled} />
            <Route exact path="/403" component={Forbidden} />
            <Route exact path="/404" component={NotFound} />
            <Route exact path="/login" component={Login} />
            <Route path="/email/:token/*" component={InboundLink} />
            <Route path="/msal" component={MSALLogin} />
            <Route path="/msal_signup" component={MSALLoginSignup} />

            <Route component={NotFound} />
          </Switch>
        </ErrorBoundary>
      </div>
    </>
  );
});

const App = () => (
  <ProfileProvider>
    <UserActivitiesProvider>
      <PublicProvider>
        <CompanyProvider>
          <DepartmentsProvider>
            <InterestsProvider>
              <SkillsProvider>
                <LocationsProvider>
                  <ClassYearsProvider>
                    <ClubsProvider>
                      <ProgramsProvider>
                        <DegreesProvider>
                          <RecognitionTypesProvider>
                            <ValuesProvider>
                              <ClickTransportProvider>
                                <BrowserRouter>
                                  <ErrorBoundary>
                                    <ModalProvider>
                                      <AppWithContext />
                                    </ModalProvider>
                                  </ErrorBoundary>
                                </BrowserRouter>
                              </ClickTransportProvider>
                            </ValuesProvider>
                          </RecognitionTypesProvider>
                        </DegreesProvider>
                      </ProgramsProvider>
                    </ClubsProvider>
                  </ClassYearsProvider>
                </LocationsProvider>
              </SkillsProvider>
            </InterestsProvider>
          </DepartmentsProvider>
        </CompanyProvider>
      </PublicProvider>
    </UserActivitiesProvider>
  </ProfileProvider>
);

export default App;
